import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { OWNER_DEPOSIT_TYPE } from "@zhihaoscm/meta";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { TABLE_COLUMN_WIDTH } from "@/config/width";
import { LINE_PAYMENT_METHOD } from "@/meta/expenseManage/shippingDeposit";
import { SHIP_SERVICE_FEE_STATE } from "@/meta/expenseManage/shippingServiceFee";
import {
    OWNER_CAPTAIN_STATE,
    PAYMENT_STATUS,
    SHIP_WAY_BILL_TYPE,
    WAYBILL_STATE,
    WAYBILL_TYPE
} from "@/meta/logistics/waybill";
import { useUserStore } from "@/stores/react";

export interface WaybillManagementQuery {
    page: number;
    size: number;
    keyword?: string;
    portKeyword?: string;
    shipKeyword?: string;
    handlerName?: string;
    state?: number[];
    type?: number;
    serviceType?: number;
    shipInfoServiceFeeState?: number[];
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface WaybillManagementRow {
    transportOrderShip: {
        id: string | number;
        srpId?: string | number;
        ownerEnterprise?: { name?: string; institutionName?: string; realName?: string };
        sourcePortName?: string;
        destinationPortName?: string;
        shipName?: string;
        unitPrice?: number;
        ton?: number;
        actualFreightCost?: number;
        state?: number;
        type?: number;
        serviceType?: number;
        upstreamHandlerName?: string;
        downstreamHandlerName?: string;
        ownerShipInfoServiceFeeState?: number;
        captainShipInfoServiceFeeState?: number;
        payType?: number;
        deposit?: number;
        isSailConfirm?: boolean;
        arrivalPortReviewState?: number;
        createdTime?: string;
    };
    shipVo?: { ship?: { cnname?: string; name?: string }; devices?: unknown[] };
    transportOrderDetailsShip?: {
        lazbInfo?: { loadVideoId?: number };
        unloadingInfo?: { unloadingVideoId?: number };
        drainage?: number;
        drainageVideoId?: number;
    };
    ownerShippingDeposits?: Array<{
        type?: number;
        state?: number;
        captainConfirmState?: number;
        thirdReviewState?: number;
    }>;
    owmerShipInfoServiceFee?: { state?: number };
    captainShipInfoServiceFee?: { state?: number };
    paidDepositFlag?: boolean;
    showDepositButton?: boolean;
    showFreightButton?: boolean;
}

export type WaybillListOperation =
    | "assign"
    | "pay-deposit"
    | "confirm-deposit"
    | "advance-pay-deposit"
    | "apply-pay-deposit"
    | "confirm-pay-deposit"
    | "pay-freight"
    | "confirm-freight"
    | "advance-pay-freight"
    | "apply-pay-freight"
    | "confirm-pay-freight"
    | "close"
    | "sail-confirm"
    | "loading-info"
    | "arrival-confirm"
    | "unloading-info"
    | "drainage-video"
    | "complete"
    | "pay-owner-service-fee"
    | "confirm-owner-service-fee"
    | "pay-captain-service-fee"
    | "confirm-captain-service-fee"
    | "monitor"
    | "edit"
    | "payment-audit"
    | "arrival-audit";

export interface WaybillListOperationItem {
    key: WaybillListOperation;
    label: string;
}

export interface WaybillManagementPageProps {
    loadWaybills?: (query: WaybillManagementQuery) => Promise<Page<WaybillManagementRow>>;
    permissions?: string[];
    banButton?: boolean;
    onOperation?: (operation: WaybillListOperation, row: WaybillManagementRow) => void;
}

/**
 * 对齐旧 Vue 列表操作列的权限和状态边界。
 * 复杂支付流程仍由详情页承接，但列表入口名称和可见条件不再退化成单一“查看”。
 */
export function getWaybillListOperations(row: WaybillManagementRow, permissions: string[]): WaybillListOperationItem[] {
    const shipOrder = row.transportOrderShip;
    const state = Number(shipOrder.state);
    const serviceType = Number(shipOrder.serviceType);
    const payType = Number(shipOrder.payType);
    const has = (permission: string) => permissions.includes(permission);
    const deposits = row.ownerShippingDeposits || [];
    const latestDeposit = [...deposits].reverse().find(item => item.type === OWNER_DEPOSIT_TYPE.DEPOSIT.code);
    const latestFreight = [...deposits]
        .reverse()
        .find(item =>
            [
                OWNER_DEPOSIT_TYPE.DEPARTURE_FEE.code,
                OWNER_DEPOSIT_TYPE.DEMURRAGE.code,
                OWNER_DEPOSIT_TYPE.FREIGHT.code
            ].includes(Number(item.type))
        );
    const operations: WaybillListOperationItem[] = [];
    const add = (key: WaybillListOperation, label: string) => operations.push({ key, label });

    if (state !== WAYBILL_STATE.COMPLETED.code && has("ROLE_SHIP_ORDER_MANAGE")) add("assign", "指派");

    if (has("ROLE_SHIP_DEMAND_OPERATE")) {
        if (
            shipOrder.deposit &&
            state === WAYBILL_STATE.PENDING_DEPOSIT.code &&
            payType === LINE_PAYMENT_METHOD.ONLINE.code
        ) {
            add("pay-deposit", deposits.length ? "重新上传支付凭证" : "支付定金");
            if (deposits.length) add("confirm-deposit", "确认收到定金");
        }
        if (
            shipOrder.deposit &&
            payType === LINE_PAYMENT_METHOD.OFFLINE.code &&
            [
                WAYBILL_STATE.PENDING_DEPOSIT.code,
                WAYBILL_STATE.AWAITING_DEPARTURE.code,
                WAYBILL_STATE.DURING_TRANSPORTATION.code
            ].includes(state)
        ) {
            const paidCount = deposits.filter(item => [1, 2, 3].includes(Number(item.type))).length;
            if (paidCount < 3) add("advance-pay-deposit", `支付第${paidCount + 1}次定金`);
            if (
                deposits.some(
                    item => [1, 2, 3].includes(Number(item.type)) && item.state === 3 && !item.captainConfirmState
                )
            ) {
                add("confirm-deposit", "确认收到定金");
            }
        }
        if (
            shipOrder.deposit &&
            row.showDepositButton &&
            payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code &&
            [
                WAYBILL_STATE.PENDING_DEPOSIT.code,
                WAYBILL_STATE.AWAITING_DEPARTURE.code,
                WAYBILL_STATE.DURING_TRANSPORTATION.code
            ].includes(state)
        ) {
            add("apply-pay-deposit", "申请支付定金");
        }
        if (shipOrder.deposit && payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code && latestDeposit?.state === 2) {
            add("confirm-pay-deposit", "确认支付定金");
        }
        if (
            shipOrder.deposit &&
            payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code &&
            latestDeposit?.state === 3 &&
            !latestDeposit.captainConfirmState
        ) {
            add("confirm-deposit", "确认收到定金");
        }
        if (
            state >= WAYBILL_STATE.TO_BE_UNLOADED.code &&
            state <= WAYBILL_STATE.COMPLETED.code &&
            payType === LINE_PAYMENT_METHOD.ONLINE.code
        ) {
            const ownFreight = deposits.find(item => item.type === OWNER_DEPOSIT_TYPE.FREIGHT.code);
            if (!ownFreight && serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code) {
                add("pay-freight", "支付运费");
            } else if (ownFreight && !ownFreight.captainConfirmState) {
                add("pay-freight", "重新上传运费凭证");
                add("confirm-freight", "确认收到运费");
            }
        }
        if (
            payType === LINE_PAYMENT_METHOD.OFFLINE.code &&
            [WAYBILL_STATE.TO_BE_UNLOADED.code, WAYBILL_STATE.COMPLETED.code].includes(state)
        ) {
            const freightCount = deposits.filter(item => [4, 5, 6].includes(Number(item.type))).length;
            if (freightCount < 3) add("advance-pay-freight", `支付第${freightCount + 1}次运费`);
            if (
                deposits.some(
                    item => [4, 5, 6].includes(Number(item.type)) && item.state === 3 && !item.captainConfirmState
                )
            ) {
                add("confirm-freight", "确认收到运费");
            }
        }
        if (
            row.showFreightButton &&
            payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code &&
            [WAYBILL_STATE.TO_BE_UNLOADED.code, WAYBILL_STATE.COMPLETED.code].includes(state)
        ) {
            add("apply-pay-freight", "申请支付运费");
        }
        if (
            payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code &&
            [WAYBILL_STATE.TO_BE_UNLOADED.code, WAYBILL_STATE.COMPLETED.code].includes(state) &&
            latestFreight?.state === 2
        ) {
            add("confirm-pay-freight", "确认支付运费");
        }
        if (
            payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code &&
            [WAYBILL_STATE.TO_BE_UNLOADED.code, WAYBILL_STATE.COMPLETED.code].includes(state) &&
            latestFreight?.state === 3 &&
            !latestFreight.captainConfirmState
        ) {
            add("confirm-freight", "确认收到运费");
        }
        if (state === WAYBILL_STATE.AWAITING_DEPARTURE.code) {
            add(
                row.transportOrderDetailsShip?.lazbInfo?.loadVideoId && !shipOrder.isSailConfirm
                    ? "sail-confirm"
                    : "loading-info",
                row.transportOrderDetailsShip?.lazbInfo?.loadVideoId && !shipOrder.isSailConfirm
                    ? "发航确认"
                    : "装货信息"
            );
        }
        if (state === WAYBILL_STATE.DURING_TRANSPORTATION.code) add("arrival-confirm", "到港确认");
        if (
            state === WAYBILL_STATE.TO_BE_UNLOADED.code &&
            !row.transportOrderDetailsShip?.unloadingInfo?.unloadingVideoId
        ) {
            add("unloading-info", "卸货信息");
        }
        if (
            row.transportOrderDetailsShip?.drainage &&
            !row.transportOrderDetailsShip.drainageVideoId &&
            [WAYBILL_STATE.DURING_TRANSPORTATION.code, WAYBILL_STATE.TO_BE_UNLOADED.code].includes(state)
        ) {
            add("drainage-video", "排水视频");
        }
        if (
            shipOrder.ownerShipInfoServiceFeeState === PAYMENT_STATUS.UNPAID.code &&
            state !== WAYBILL_STATE.PENDING_DEPOSIT.code &&
            serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code
        ) {
            add("pay-owner-service-fee", "支付货主服务费");
        }
        if (
            shipOrder.captainShipInfoServiceFeeState === PAYMENT_STATUS.UNPAID.code &&
            state !== WAYBILL_STATE.PENDING_DEPOSIT.code
        ) {
            add("pay-captain-service-fee", "支付船主服务费");
        }
        if (
            row.owmerShipInfoServiceFee?.state === SHIP_SERVICE_FEE_STATE.UN_CONFIRM.code &&
            serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code
        ) {
            add("confirm-owner-service-fee", "确认货主服务费");
        }
        if (row.captainShipInfoServiceFee?.state === SHIP_SERVICE_FEE_STATE.UN_CONFIRM.code) {
            add("confirm-captain-service-fee", "确认船主服务费");
        }
    }

    const isMatchingClose =
        shipOrder.captainShipInfoServiceFeeState === PAYMENT_STATUS.UNPAID.code &&
        serviceType === SHIP_WAY_BILL_TYPE.MATCHING.code &&
        state === WAYBILL_STATE.UNCOMPLETED.code;
    const isSupervisionClose =
        serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code && state === WAYBILL_STATE.PENDING_DEPOSIT.code;
    if (has("ROLE_SHIP_ORDER_CLOSE") && (isMatchingClose || isSupervisionClose)) add("close", "关闭");

    if (
        has("ROLE_SHIP_ORDER_MANAGE") &&
        (state === WAYBILL_STATE.UNCOMPLETED.code ||
            (state === WAYBILL_STATE.TO_BE_UNLOADED.code &&
                !!row.transportOrderDetailsShip?.unloadingInfo?.unloadingVideoId))
    ) {
        add("complete", "完成");
    }
    if (row.shipVo?.devices?.length) add("monitor", "监控");
    if (
        has("ROLE_SHIP_ORDER_DEAL") &&
        ((serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code && state === WAYBILL_STATE.DURING_TRANSPORTATION.code) ||
            (serviceType === SHIP_WAY_BILL_TYPE.MATCHING.code && state === WAYBILL_STATE.UNCOMPLETED.code))
    ) {
        add("edit", "修改");
    }
    if (has("ROLE_YUJIANG_REV_W") && row.ownerShippingDeposits?.some(item => item.thirdReviewState === 1)) {
        add("payment-audit", "支付审核");
    }
    if (has("ROLE_YUJIANG_REV_W") && shipOrder.arrivalPortReviewState === 1) add("arrival-audit", "到港审核");
    return operations;
}

/** 复用 Vue 船运单管理分页接口，保留历史查询参数。 */
export async function loadWaybillManagement(query: WaybillManagementQuery): Promise<Page<WaybillManagementRow>> {
    const response = await shippingRequestHttp.service.get<Page<WaybillManagementRow>>("/transport/order/ship/paging", {
        params: query
    });
    return response.data;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 将旧 Vue daterange 的日期值转换为船运单分页接口的全天边界。 */
function toWaybillDateTime(value: string | undefined, end = false): string | undefined {
    return value ? `${value}T${end ? "23:59:59" : "00:00:00"}` : undefined;
}

/** 按旧 Vue ZhTable 的字段名构造船运单筛选请求。 */
export function buildWaybillManagementQuery(
    filters: WaybillManagementQuery,
    createdTimeRange: [string, string] | null
): WaybillManagementQuery {
    const nextQuery: WaybillManagementQuery = { page: 1, size: 10 };
    Object.entries(filters).forEach(([key, value]) => {
        if (key === "page" || key === "size" || value === "" || value == null) return;
        if (Array.isArray(value) && value.length === 0) return;
        nextQuery[key as keyof WaybillManagementQuery] = value as never;
    });
    const beginTime = toWaybillDateTime(createdTimeRange?.[0]);
    const endTime = toWaybillDateTime(createdTimeRange?.[1], true);
    if (beginTime) nextQuery.beginTime = beginTime;
    if (endTime) nextQuery.endTime = endTime;
    return nextQuery;
}

/** 船运单管理列表，完整复用 Vue 的字段、筛选、分页和操作入口。 */
export function WaybillManagementPage({
    loadWaybills = loadWaybillManagement,
    permissions,
    banButton,
    onOperation
}: WaybillManagementPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const operationPermissions = permissions ?? storePermissions;
    const isButtonBanned = banButton ?? storeBanButton;
    const [rows, setRows] = useState<WaybillManagementRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<WaybillManagementQuery>({ page: 1, size: 10 });
    const [filters, setFilters] = useState<WaybillManagementQuery>({ page: 1, size: 10 });
    // 保留旧 Vue 的 triggerTime 本地筛选值，查询时再转换为 beginTime/endTime。
    const [createdTimeRange, setCreatedTimeRange] = useState<[string, string] | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadWaybills(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("船运单列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadWaybills, query]);

    const submitFilters = () => {
        setQuery(buildWaybillManagementQuery(filters, createdTimeRange));
    };

    const resetFilters = () => {
        setFilters({ page: 1, size: 10 });
        setCreatedTimeRange(null);
        setQuery({ page: 1, size: 10 });
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 船运单 ZhTable 的字段与操作插槽。
    const waybillColumns = useMemo<TableColumnProps<WaybillManagementRow>[]>(
        () => [
            {
                key: "id",
                label: "运单编号",
                width: TABLE_COLUMN_WIDTH.WORD7,
                prop: "transportOrderShip.id",
                render: ({ row }) => (
                    <Link to={`/logistics/shippingNeed/waybillManagementInfo/${row.transportOrderShip.id}`}>
                        {text(row.transportOrderShip.id)}
                    </Link>
                )
            },
            {
                key: "type",
                label: "船运单生成类型",
                width: TABLE_COLUMN_WIDTH.WORD7,
                prop: "transportOrderShip.type",
                render: ({ row }) => WAYBILL_TYPE.getIntroByCode(row.transportOrderShip.type) || DEFAULT_VALUE
            },
            {
                key: "owner",
                label: "货主",
                minWidth: TABLE_COLUMN_WIDTH.WORD6,
                prop: "transportOrderShip.ownerEnterprise",
                render: ({ row }) =>
                    text(
                        row.transportOrderShip.ownerEnterprise?.name || row.transportOrderShip.ownerEnterprise?.realName
                    )
            },
            {
                key: "source",
                label: "始发地",
                minWidth: TABLE_COLUMN_WIDTH.WORD8,
                prop: "transportOrderShip.sourcePortName",
                render: ({ row }) => text(row.transportOrderShip.sourcePortName)
            },
            {
                key: "destination",
                label: "目的地",
                minWidth: TABLE_COLUMN_WIDTH.WORD8,
                prop: "transportOrderShip.destinationPortName",
                render: ({ row }) => text(row.transportOrderShip.destinationPortName)
            },
            {
                key: "ship",
                label: "船舶名称",
                minWidth: TABLE_COLUMN_WIDTH.WORD8,
                prop: "shipVo.ship.cnname",
                render: ({ row }) => text(row.transportOrderShip.shipName?.split("/")[0])
            },
            {
                key: "upstreamHandlerName",
                label: "上游专员",
                width: TABLE_COLUMN_WIDTH.WORD4,
                prop: "transportOrderShip.upstreamHandlerName",
                render: ({ row }) => text(row.transportOrderShip.upstreamHandlerName)
            },
            {
                key: "downstreamHandlerName",
                label: "下游专员",
                width: TABLE_COLUMN_WIDTH.WORD4,
                prop: "transportOrderShip.downstreamHandlerName",
                render: ({ row }) => text(row.transportOrderShip.downstreamHandlerName)
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: TABLE_COLUMN_WIDTH.WORD8,
                prop: "transportOrderShip.createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.transportOrderShip.createdTime)
            },
            {
                key: "shipInfoServiceFeeState",
                label: "信息服务费支付状态",
                width: TABLE_COLUMN_WIDTH.WORD12,
                prop: "transportOrderShip.shipInfoServiceFeeState",
                render: ({ row }) => {
                    const ownerState = row.transportOrderShip.ownerShipInfoServiceFeeState;
                    const captainState = row.transportOrderShip.captainShipInfoServiceFeeState;
                    if (!ownerState && !captainState) return DEFAULT_VALUE;
                    return (
                        <Space className="ship-info-service-fee-state" direction="vertical" size={0}>
                            {ownerState ? (
                                <span className={`pay-state-${ownerState}`}>
                                    <span className="pay-state-dot" />
                                    货主{PAYMENT_STATUS.getIntroByCode(ownerState)}
                                </span>
                            ) : null}
                            {captainState ? (
                                <span className={`pay-state-${captainState}`}>
                                    <span className="pay-state-dot" />
                                    船主{PAYMENT_STATUS.getIntroByCode(captainState)}
                                </span>
                            ) : null}
                        </Space>
                    );
                }
            },
            {
                key: "serviceType",
                label: "服务类型",
                width: TABLE_COLUMN_WIDTH.WORD6,
                prop: "transportOrderShip.serviceType",
                render: ({ row }) =>
                    SHIP_WAY_BILL_TYPE.getIntroByCode(row.transportOrderShip.serviceType) || DEFAULT_VALUE
            },
            {
                key: "state",
                label: "运单状态",
                width: TABLE_COLUMN_WIDTH.WORD9,
                prop: "transportOrderShip.state",
                render: ({ row }) => (
                    <div className="status-col">
                        <div>{WAYBILL_STATE.getIntroByCode(row.transportOrderShip.state) || DEFAULT_VALUE}</div>
                    </div>
                )
            },
            {
                key: "srpId",
                label: "关联需求编号",
                width: TABLE_COLUMN_WIDTH.WORD7,
                prop: "transportOrderShip.srpId",
                render: ({ row }) => {
                    const srpId = row.transportOrderShip.srpId;
                    if (srpId == null || srpId === "") return text(srpId);
                    // 沿用 Vue goPlatformShipNeedInfo，保留来源参数供需求详情返回船运单列表。
                    return (
                        <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${srpId}?from=waybillManagement`}>
                            {text(srpId)}
                        </Link>
                    );
                }
            },
            {
                key: "operation",
                label: "操作",
                minWidth: 185,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => {
                    const operations = getWaybillListOperations(row, operationPermissions);
                    return (
                        <Space size={0} wrap>
                            {operations.map(operation => {
                                const actionPath =
                                    operation.key === "edit"
                                        ? `/logistics/shippingNeed/waybillManagementEdit/${row.transportOrderShip.id}`
                                        : `/logistics/shippingNeed/waybillManagementInfo/${row.transportOrderShip.id}?action=${operation.key}`;
                                return onOperation ? (
                                    <Button
                                        key={operation.key}
                                        disabled={isButtonBanned}
                                        type="link"
                                        onClick={() => onOperation(operation.key, row)}
                                    >
                                        {operation.label}
                                    </Button>
                                ) : (
                                    <Link
                                        key={operation.key}
                                        aria-disabled={isButtonBanned}
                                        onClick={event => {
                                            if (isButtonBanned) event.preventDefault();
                                        }}
                                        to={actionPath}
                                    >
                                        {operation.label}
                                    </Link>
                                );
                            })}
                        </Space>
                    );
                }
            }
        ],
        [isButtonBanned, onOperation, operationPermissions]
    );

    // 保留 Vue 船运单列表对创建时间的 sortKey/sortOrder 请求契约。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "transportOrderShip.createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="waybill-management-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhTable<WaybillManagementRow>
                    rowKey={row => String(row.transportOrderShip.id)}
                    data={rows}
                    columns={normalizeOperationColumns(waybillColumns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4 w-full" align="center">
                                <Typography.Title className="mb-0" level={4}>
                                    船运单管理
                                </Typography.Title>
                                <Button
                                    aria-label="新增船运单"
                                    disabled={isButtonBanned}
                                    type="primary"
                                    onClick={() => navigate("/logistics/shippingNeed/waybillManagementAdd")}
                                >
                                    新增
                                </Button>
                            </Space>

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item>
                                    <Input
                                        aria-label="货主名称或运单编号"
                                        placeholder="请输入货主名称或运单编号搜索"
                                        value={filters.keyword || ""}
                                        onChange={event =>
                                            setFilters(current => ({ ...current, keyword: event.target.value }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Input
                                        aria-label="码头关键词"
                                        placeholder="请输入码头关键词搜索"
                                        value={filters.portKeyword || ""}
                                        onChange={event =>
                                            setFilters(current => ({ ...current, portKeyword: event.target.value }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Input
                                        aria-label="船舶名称关键词"
                                        placeholder="请输入船舶名称关键词搜索"
                                        value={filters.shipKeyword || ""}
                                        onChange={event =>
                                            setFilters(current => ({ ...current, shipKeyword: event.target.value }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Input
                                        aria-label="船务专员"
                                        placeholder="请输入船务专员搜索"
                                        value={filters.handlerName || ""}
                                        onChange={event =>
                                            setFilters(current => ({ ...current, handlerName: event.target.value }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Select
                                        allowClear
                                        aria-label="船运单生成类型"
                                        options={WAYBILL_TYPE.options}
                                        placeholder="请选择船运单生成类型"
                                        value={filters.type}
                                        onChange={value => setFilters(current => ({ ...current, type: value }))}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Select
                                        allowClear
                                        aria-label="运单状态"
                                        mode="multiple"
                                        options={WAYBILL_STATE.options}
                                        placeholder="请选择运单状态"
                                        value={filters.state}
                                        onChange={value => setFilters(current => ({ ...current, state: value }))}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <ZhDateRangePicker
                                        aria-label="创建时间"
                                        endPlaceholder="创建结束时间"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="创建开始时间"
                                        type="daterange"
                                        value={createdTimeRange}
                                        valueFormat="YYYY-MM-DD"
                                        onChange={(_value, formattedValue) =>
                                            setCreatedTimeRange(
                                                Array.isArray(formattedValue) && formattedValue.length === 2
                                                    ? [formattedValue[0], formattedValue[1]]
                                                    : null
                                            )
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Select
                                        allowClear
                                        aria-label="信息服务费支付状态"
                                        mode="multiple"
                                        options={OWNER_CAPTAIN_STATE.options}
                                        placeholder="请选择信息服务费支付状态"
                                        value={filters.shipInfoServiceFeeState}
                                        onChange={value =>
                                            setFilters(current => ({ ...current, shipInfoServiceFeeState: value }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Select
                                        allowClear
                                        aria-label="服务类型"
                                        options={SHIP_WAY_BILL_TYPE.options}
                                        placeholder="请选择服务类型"
                                        value={filters.serviceType}
                                        onChange={value => setFilters(current => ({ ...current, serviceType: value }))}
                                    />
                                </Form.Item>
                                <Button aria-label="查询运单" htmlType="submit" type="primary">
                                    搜索
                                </Button>
                                <Button aria-label="重置" onClick={resetFilters}>
                                    重置
                                </Button>
                            </Form>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({ ...current, page, size: pageSize }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default WaybillManagementPage;
